/** Pure helpers for editing one active exercise in the full-screen editor. */
import { type ActiveExercise, type ActiveSet, createEmptySet } from "./activeWorkout";

export const WEIGHT_STEP = 2.5;
export const REPS_STEP = 1;

/** Weight stepper: half-kg grid, never below 0. */
export function nudgeWeight(weight: number | null, delta: number): number {
  return Math.max(0, Math.round(((weight ?? 0) + delta) * 2) / 2);
}

/** Reps stepper: never below 1. */
export function nudgeReps(reps: number, delta: number): number {
  return Math.max(1, Math.round(reps + delta));
}

/** New set after the last one, starting from its values. */
export function appendSet(ex: ActiveExercise): ActiveExercise {
  const last = ex.sets[ex.sets.length - 1];
  return {
    ...ex,
    sets: [
      ...ex.sets,
      createEmptySet({
        reps: last?.reps ?? 10,
        weight: last?.weight ?? null,
        unit: last?.unit ?? "kg",
      }),
    ],
  };
}

/** Copy of a set (not done) right after the original. */
export function duplicateSet(ex: ActiveExercise, setId: string): ActiveExercise {
  const i = ex.sets.findIndex((s) => s.id === setId);
  if (i < 0) return ex;
  const src = ex.sets[i]!;
  const copy = createEmptySet({ reps: src.reps, weight: src.weight, unit: src.unit });
  return { ...ex, sets: [...ex.sets.slice(0, i + 1), copy, ...ex.sets.slice(i + 1)] };
}

/** Remove a set; the last remaining set cannot be removed. */
export function removeSetById(ex: ActiveExercise, setId: string): ActiveExercise {
  if (ex.sets.length <= 1) return ex;
  return { ...ex, sets: ex.sets.filter((s) => s.id !== setId) };
}

export function patchSetById(
  ex: ActiveExercise,
  setId: string,
  patch: Partial<ActiveSet>,
): ActiveExercise {
  return { ...ex, sets: ex.sets.map((s) => (s.id === setId ? { ...s, ...patch } : s)) };
}

/** True when the draft differs from what was opened (the UI-only `invalid` flag is ignored). */
export function isDraftDirty(initial: ActiveExercise, draft: ActiveExercise): boolean {
  const norm = (e: ActiveExercise) => JSON.stringify({ ...e, invalid: undefined });
  return norm(initial) !== norm(draft);
}
